(() => {
  if (customElements.get('scroll-to')) {
    return;
  }

  class ScrollTo extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.attachEvents();
    }

    attachEvents() {
      this.addEventListener('click', this.scrollTo.bind(this));
    }

    scrollTo(event) {
      const element = document.querySelector(this.dataset.elementId);

      if (!element) {
        return;
      }

      if (this.dataset.elementId === '#pdp-specification' || this.dataset.elementId === '#pdp-description') {
        const tabPanel = element.closest('.wprod-details.is-tabbed .accordion');
        if (tabPanel) {
          const tab = document.querySelector('[aria-controls="' + tabPanel.id + '"]');
          tab?.click();
          requestAnimationFrame(() => this.scrollToElement(tabPanel.closest('.wprod-details')));
          event.preventDefault();
          return;
        }

        const summary = element.querySelector('summary');
        const details = summary ? summary.closest('details') : null;

        if (details && !details.open) {
          summary.click();
        }

        const scrollTarget = this.dataset.elementId === '#pdp-description'
          ? element.closest('.wprod-details') || element
          : element;
        requestAnimationFrame(() => this.scrollToElement(scrollTarget));

        event.preventDefault();

        return;
      }

      this.scrollToElement(element);
      event.preventDefault();
    }

    scrollToElement(element) {
      const announcementBar = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--topbar-height'), 0);
      const headerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height'), 0);
      const stickyHeaderHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--sticky-header-height'), 0);
      const finalHeaderHeight = stickyHeaderHeight ? stickyHeaderHeight : headerHeight;

      window.scrollTo({
        top: element.getBoundingClientRect().top + window.scrollY - (finalHeaderHeight + announcementBar),
        behavior: theme.config.motionReduced ? 'auto' : 'smooth'
      });
    }
  }

  customElements.define('scroll-to', ScrollTo);
})();

(() => {
  const desktopProductDetails = window.matchMedia('(min-width: 1024px)');
  const tabbedSections = new WeakMap();

  function initialiseProductTabs(section) {
    if (tabbedSections.has(section)) return;

    const accordions = Array.from(section.querySelectorAll(':scope > accordions-details > .accordion'))
      .filter((accordion) => getComputedStyle(accordion).display !== 'none');
    if (accordions.length < 2) return;

    const list = document.createElement('div');
    list.className = 'wprod-details__tablist';
    list.setAttribute('role', 'tablist');
    list.setAttribute('aria-label', section.querySelector('.wprod-details__heading')?.textContent.trim() || 'Product details');

    const originalState = accordions.map((accordion) => {
      const details = accordion.querySelector('details');
      return {
        accordion,
        details,
        id: accordion.getAttribute('id'),
        role: accordion.getAttribute('role'),
        labelledBy: accordion.getAttribute('aria-labelledby'),
        tabIndex: accordion.getAttribute('tabindex'),
        hidden: accordion.hasAttribute('hidden'),
        open: details?.hasAttribute('open')
      };
    });

    const buttons = accordions.map((accordion, index) => {
      const details = accordion.querySelector('details');
      const heading = details?.querySelector('summary .heading');
      const button = document.createElement('button');
      const panelId = `${section.querySelector('.wprod-details__heading')?.id || 'ProductDetails'}-panel-${index}`;

      accordion.id = panelId;
      accordion.setAttribute('role', 'tabpanel');
      accordion.setAttribute('aria-labelledby', `${panelId}-tab`);
      accordion.tabIndex = 0;
      accordion.hidden = index !== 0;
      details?.setAttribute('open', '');

      button.type = 'button';
      button.className = 'wprod-details__tab';
      button.id = `${panelId}-tab`;
      button.textContent = heading?.textContent.trim() || `Details ${index + 1}`;
      button.setAttribute('role', 'tab');
      button.setAttribute('aria-controls', panelId);
      button.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
      button.tabIndex = index === 0 ? 0 : -1;
      list.appendChild(button);
      return button;
    });

    function activate(index, focus = false) {
      buttons.forEach((button, position) => {
        const active = position === index;
        button.setAttribute('aria-selected', active ? 'true' : 'false');
        button.tabIndex = active ? 0 : -1;
        accordions[position].hidden = !active;
      });
      if (focus) buttons[index].focus();
    }

    buttons.forEach((button, index) => {
      button.addEventListener('click', () => activate(index));
      button.addEventListener('keydown', (event) => {
        let next;
        if (event.key === 'ArrowRight') next = (index + 1) % buttons.length;
        if (event.key === 'ArrowLeft') next = (index - 1 + buttons.length) % buttons.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = buttons.length - 1;
        if (next === undefined) return;
        event.preventDefault();
        activate(next, true);
      });
    });

    section.querySelector('accordions-details').before(list);
    section.classList.add('is-tabbed');
    tabbedSections.set(section, { list, originalState });
  }

  function restoreProductAccordions(section) {
    const state = tabbedSections.get(section);
    if (!state) return;

    section.classList.remove('is-tabbed');
    state.list.remove();
    state.originalState.forEach(({ accordion, details, id, role, labelledBy, tabIndex, hidden, open }) => {
      if (id === null) accordion.removeAttribute('id');
      else accordion.setAttribute('id', id);
      if (role === null) accordion.removeAttribute('role');
      else accordion.setAttribute('role', role);
      if (labelledBy === null) accordion.removeAttribute('aria-labelledby');
      else accordion.setAttribute('aria-labelledby', labelledBy);
      if (tabIndex === null) accordion.removeAttribute('tabindex');
      else accordion.setAttribute('tabindex', tabIndex);
      accordion.hidden = hidden;
      details?.toggleAttribute('open', open);
    });
    tabbedSections.delete(section);
  }

  function updateProductDetails(root = document) {
    const sections = root.matches?.('.wprod-details') ? [root] : root.querySelectorAll('.wprod-details');
    sections.forEach((section) => {
      if (desktopProductDetails.matches) initialiseProductTabs(section);
      else restoreProductAccordions(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => updateProductDetails());
  } else {
    updateProductDetails();
  }
  // Safari before 14 only has the older addListener on media queries.
  if (desktopProductDetails.addEventListener) desktopProductDetails.addEventListener('change', () => updateProductDetails());
  else if (desktopProductDetails.addListener) desktopProductDetails.addListener(() => updateProductDetails());
  document.addEventListener('shopify:section:load', (event) => updateProductDetails(event.target));
})();

(() => {
  // Klarna "Click for more info" on product cards opens a popup, like
  // Zopa's widget does, instead of leaving the collection (Luke, 10
  // Sep). The href stays as the no-JS fallback and the dialog links to
  // the full Klarna page. The Pay in 3 line and the warning under it
  // are Klarna's pre-approved UK copy, word for word, at the same size
  // (Monday 3245834177, 5 Oct): Klarna allows no edits, wants the
  // warning directly beside the offer, and leaves instalment amounts
  // to its own On-site messaging, so the popup quotes no figure.
  let dialog = null;

  const build = () => {
    dialog = document.createElement('dialog');
    dialog.className = 'wklarna__dialog';
    dialog.setAttribute('aria-labelledby', 'wklarna-title');
    dialog.innerHTML =
      '<button type="button" class="wklarna__close" aria-label="Close">&times;</button>' +
      '<h3 class="wklarna__title" id="wklarna-title">Pay with Klarna</h3>' +
      '<p class="wklarna__lead">Split your purchase into 3 interest-free instalments with Klarna</p>' +
      '<p>Borrowing more than you can afford or paying late may negatively impact your financial status and ability to obtain credit. Subject to status. <a href="https://www.klarna.com/uk/terms-and-conditions/">Ts&amp;Cs</a> and late fees apply.</p>' +
      '<p class="wklarna__note">18+, UK residents only. Select Klarna when you choose how to pay at checkout. <a href="/pages/klarna">Read more about Klarna at Waltons</a>.</p>';
    document.body.appendChild(dialog);
    dialog.querySelector('.wklarna__close').addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', (e) => {
      // A click on the backdrop lands on the dialog element itself; one
      // in the dialog's own padding does too, so only outside its box
      // closes it (as the Zopa popup below).
      if (e.target !== dialog) return;
      const r = dialog.getBoundingClientRect();
      if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) dialog.close();
    });
  };

  document.addEventListener('click', (e) => {
    const link = e.target.closest('[data-klarna-info]');
    if (!link) return;
    e.preventDefault();
    if (!dialog) build();
    dialog.showModal();
  });
})();

(() => {
  // Zopa "Click for more info" on the Zopa fallback band (cards, product
  // page, options drawer and basket; snippets/zopa-fallback.liquid) opens
  // this popup, built like the Klarna one above (Luke, 19 Oct: "Can the
  // design not be like Klarna with the popup?"). The band only shows
  // while Zopa's widget is down, so this is the shopper's only Zopa
  // information on the page.
  // Kept to the Klarna popup's four parts (title, lead, the how and the
  // caution, then the note with Read more), so it reads as a quick note,
  // not a page. Every sentence is from the live finance page,
  // /pages/waltons-finance-info, word for word (19 Oct 2026): the
  // "Spread the cost with Zopa" heading, the first line of the "Are there
  // limits" answer, How it works step 1, the "Will a credit check be
  // performed?" answer's full check line (in the place of Klarna's
  // warning, so the popup isn't all ease) and the closing note. Who can
  // apply, the steps and the costs stay on the page behind Read more. No
  // rates, interest free plans or monthly figures: the page sets those
  // beside its own detail, and Zopa shows the exact cost at checkout.
  // Change them only from that page.
  // The "Read more" link takes the band link's href (the theme's Finance
  // link setting) and, from the options drawer, its new tab, so the
  // shopper's options are kept. Without JavaScript, or with a modifier
  // key, the band link goes straight to the page.
  let dialog = null;
  let more = null;

  const build = () => {
    dialog = document.createElement('dialog');
    dialog.className = 'wzopa__dialog';
    dialog.setAttribute('aria-labelledby', 'wzopa-title');
    dialog.innerHTML =
      '<button type="button" class="wzopa__close" aria-label="Close">&times;</button>' +
      '<h3 class="wzopa__title" id="wzopa-title">Spread the cost with Zopa</h3>' +
      '<p class="wzopa__lead">Zopa credit is available on orders from £300 to £12,500.</p>' +
      '<p>Go to checkout and select Zopa. A full credit check, which does leave a record, is only performed if you go on to complete the application.</p>' +
      '<p class="wzopa__note">All finance applications are subject to status, affordability checks, and Zopa’s terms and conditions. <a class="wzopa__more" href="/pages/waltons-finance-info">Read more about finance at Waltons</a>.</p>';
    document.body.appendChild(dialog);
    more = dialog.querySelector('.wzopa__more');
    dialog.querySelector('.wzopa__close').addEventListener('click', () => dialog.close());
    // Escape closes the dialog on keydown, but the theme's drawers close
    // on Escape's keyup at the document (theme.js), so over the options
    // drawer one press closed both. The keyup that follows is kept from
    // them, so only the popup closes.
    // If that keyup never comes (the window lost focus mid-press), the
    // window's blur drops the guard, so it can't eat a later Escape.
    const drop = () => {
      window.removeEventListener('keyup', swallow, true);
      window.removeEventListener('blur', drop);
    };
    const swallow = (k) => {
      if (k.key !== 'Escape') return;
      k.stopPropagation();
      drop();
    };
    dialog.addEventListener('keydown', (k) => {
      if (k.key !== 'Escape') return;
      window.addEventListener('keyup', swallow, true);
      window.addEventListener('blur', drop);
    });
    dialog.addEventListener('click', (e) => {
      // A click on the backdrop lands on the dialog element itself; one
      // in the dialog's own padding does too, so only outside its box
      // closes it.
      if (e.target !== dialog) return;
      const r = dialog.getBoundingClientRect();
      if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) dialog.close();
    });
  };

  document.addEventListener('click', (e) => {
    const link = e.target.closest('[data-zopa-info]');
    if (!link || e.button || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    if (typeof HTMLDialogElement !== 'function') return;
    e.preventDefault();
    if (!dialog) build();
    more.setAttribute('href', link.getAttribute('href') || '/pages/waltons-finance-info');
    if (link.target === '_blank') {
      more.target = '_blank';
      more.rel = 'noopener';
    } else {
      more.removeAttribute('target');
      more.removeAttribute('rel');
    }
    if (!dialog.open) dialog.showModal();
  });
})();

(() => {
  // Zopa's SDK hides a card's widget itself when the quote can't be
  // fetched. If the SDK never arrives (Zopa's script host down or
  // blocked), nothing draws or hides the widgets and each keeps the
  // space held for its band, so they're dropped the same way
  // (theme.css, .zopa-unavailable). The load event waits for the SDK's
  // script, so by then it has either defined the element or failed.
  // theme.liquid sets the same class sooner when the script errors; this
  // check still covers a script that loads but defines nothing.
  const check = () => {
    if (!document.querySelector('script[data-dividebuy-api-key]')) return;
    if (!window.customElements || customElements.get('dividebuy-widget')) return;
    document.documentElement.classList.add('zopa-unavailable');
  };
  if (document.readyState === 'complete') check();
  else window.addEventListener('load', check);
  // The flag now also shows the Zopa fallback line, so one raised in error
  // (an SDK defined after the load event) is dropped once the element
  // exists. A script that failed can never define it, so this only ever
  // undoes a false alarm.
  if (window.customElements && customElements.whenDefined) {
    customElements.whenDefined('dividebuy-widget').then(() => {
      document.documentElement.classList.remove('zopa-unavailable');
    });
  }
})();

(() => {
  // Zopa fallback line (snippets/zopa-fallback.liquid, Luke 19 Oct). CSS
  // shows it once html.zopa-unavailable is set (Zopa's script failed,
  // above). This covers the other failure: the script loads but a widget
  // draws no quote, as when Zopa's quote service is down. A widget whose
  // amount is in Zopa's £300 to £12,500 range counts as failed (and gets
  // .is-zopa-failed on its line) when:
  // - Zopa's SDK has hidden it, 1.5 seconds after the load event or its
  //   last amount change. The SDK sets display: none on the element itself
  //   when its quote request fails, and only visibility: hidden while a
  //   quote is on its way, so a slow quote is never taken for a failed one;
  // - or it has drawn nothing 15 seconds on: a quote request that hangs.
  // The line goes the moment a quote lands (each widget's shadow root is
  // watched), before the next paint, so the two never show together.
  // While Zopa works its quote is there long before either mark, so
  // nothing changes. Also keeps the product page line off variants over
  // £12,500 (.is-out-of-range), where Zopa isn't offered. The options
  // drawer has its own copy of this (product-configurator.liquid).
  // The basket's widget is built once, at page load, by the Zopa app's own
  // script, which doesn't run again when the basket is redrawn after a
  // change; from then on only html.zopa-unavailable shows the basket line.
  const MIN = 300;
  const MAX = 12500;
  const SETTLE = 1500;
  const BACKSTOP = 15000;
  const lines = new Set();
  const watched = new WeakSet();
  const shadowWatched = new WeakSet();
  let loaded = document.readyState === 'complete';
  // The 1.5 and 15 seconds count from when Zopa's script has had its
  // chance (the load event, or the SDK arriving later than that), or from
  // when the line or the widget's amount last changed, whichever is later.
  let readyAt = 0;
  let timer = 0;
  let scanTimer = 0;

  // A card's line shares the finance strip with its widget; the product
  // page and basket lines follow the Zopa app block holding theirs.
  const widgetFor = (line) => {
    const box = line.dataset.zopaFallback === 'card' ? line.parentElement : line.previousElementSibling;
    return box ? box.querySelector('dividebuy-widget') : null;
  };
  // Zopa draws its quote as .dividebuy-widget in the element's shadow
  // root. The element's own computed display (unlike its box) doesn't
  // depend on its ancestors, so a widget in a hidden container still reads
  // right, and the card CSS that folds a failed widget away keeps it.
  const hiddenBySdk = (w) => getComputedStyle(w).display === 'none';
  const quoted = (w) => !!(w.shadowRoot && w.shadowRoot.querySelector('.dividebuy-widget')) && !hiddenBySdk(w);
  const sdkReady = () => !!(window.customElements && customElements.get('dividebuy-widget'));

  // Any change to a watched widget (its amount, Zopa hiding or showing
  // it, a quote landing in its shadow root) gets a look straight away.
  const changed = 'MutationObserver' in window ? new MutationObserver((records) => {
    records.forEach((r) => {
      if (r.type !== 'attributes' || r.attributeName !== 'amount') return;
      lines.forEach((line) => { if (widgetFor(line) === r.target) line.zopaSince = Date.now(); });
    });
    run();
  }) : null;
  const watch = (w) => {
    if (!changed) return;
    if (!watched.has(w)) {
      watched.add(w);
      changed.observe(w, { attributes: true, attributeFilter: ['amount', 'style', 'class', 'hidden'] });
    }
    if (w.shadowRoot && !shadowWatched.has(w)) {
      shadowWatched.add(w);
      changed.observe(w.shadowRoot, { childList: true, subtree: true });
    }
  };

  // Returns how long until this line wants another look, or 0.
  const look = (line) => {
    const w = widgetFor(line);
    if (!w) {
      line.classList.remove('is-zopa-failed');
      return 0;
    }
    watch(w);
    const amount = parseFloat(w.getAttribute('amount'));
    if (line.dataset.zopaFallback === 'product') line.classList.toggle('is-out-of-range', amount > MAX);
    if (!loaded || !sdkReady() || !(amount >= MIN && amount <= MAX) || quoted(w)) {
      line.classList.remove('is-zopa-failed');
      return 0;
    }
    if (!readyAt) readyAt = Date.now();
    const age = Date.now() - Math.max(readyAt, line.zopaSince || 0);
    const due = hiddenBySdk(w) ? SETTLE : BACKSTOP;
    if (age >= due) {
      line.classList.add('is-zopa-failed');
      // A late quote is caught by the shadow root watch; until the widget
      // has a shadow root to watch, keep looking.
      return w.shadowRoot && changed ? 0 : 1000;
    }
    // Still waiting: look each second, since Zopa may hide it before then.
    return Math.min(1000, due - age);
  };

  function run() {
    clearTimeout(timer);
    timer = 0;
    let next = 0;
    lines.forEach((line) => {
      if (!line.isConnected) {
        lines.delete(line);
        return;
      }
      const wait = look(line);
      if (wait && (!next || wait < next)) next = wait;
    });
    if (next) timer = setTimeout(run, next);
  }
  function schedule(ms) {
    clearTimeout(timer);
    timer = setTimeout(run, ms);
  }

  // Lines arrive with the page, a filtered or paged collection grid, or
  // a re-rendered basket, and a widget can arrive after its line (the
  // Zopa app builds the product page's at DOMContentLoaded); either gets
  // a look. The options drawer's line is left to its own script.
  const scan = () => {
    scanTimer = 0;
    let due = false;
    document.querySelectorAll('[data-zopa-fallback]:not([data-zopa-fallback="configurator"])').forEach((line) => {
      if (!lines.has(line)) {
        lines.add(line);
        line.zopaSince = Date.now();
        due = true;
        return;
      }
      const w = widgetFor(line);
      if (w && !watched.has(w)) due = true;
    });
    if (due) schedule(0);
  };

  // Only pages that can hold a line keep watching for new ones: product
  // pages and the basket (the Zopa app's block) and collection grids
  // (cards come and go with filters and paging).
  const start = () => {
    scan();
    if (!('MutationObserver' in window)) return;
    if (!document.querySelector('[data-zopa-fallback], .dividebuy_widget_block, script[data-dividebuy-api-key], #ProductGridContainer')) return;
    new MutationObserver(() => {
      if (!scanTimer) scanTimer = setTimeout(scan, 250);
    }).observe(document.body, { childList: true, subtree: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();

  // Both of these start the clock (readyAt, set at the first look once
  // both are in).
  const onLoad = () => {
    loaded = true;
    schedule(0);
  };
  if (loaded) onLoad();
  else window.addEventListener('load', onLoad);
  if (window.customElements && customElements.whenDefined) {
    customElements.whenDefined('dividebuy-widget').then(() => schedule(0));
  }
})();

(() => {
  // Klarna card band rows: at 640 to 749px (two cards a row) Zopa's
  // band has no fixed 87px height and can wrap taller than "Pay with
  // Klarna", so every finance band in a row takes the tallest one's
  // height and the "Configure yours" buttons stay level. Cards in a
  // grid row are already equal height, but the features list under the
  // button varies, so CSS alone can't line the bands up.
  const twoUp = window.matchMedia('(min-width: 640px) and (max-width: 749px)');
  let frame = 0;
  const watched = new Set();
  const ro = 'ResizeObserver' in window ? new ResizeObserver(() => schedule()) : null;

  // A Zopa widget with no box was hidden because its quote failed (or
  // the SDK never loaded, see above), so the card shows no band: it
  // stays out of the row and keeps no height, as it does from 750px.
  // One still waiting for its quote is only visibility: hidden and
  // holds its space, so it's levelled as usual.
  // A card showing the Zopa fallback line in its place has a band again.
  const noBand = (w) => {
    const line = w.querySelector('.zopa-fallback');
    if (line && line.getClientRects().length > 0) return false;
    const z = w.querySelector('dividebuy-widget');
    return !!z && z.getClientRects().length === 0;
  };

  const level = () => {
    frame = 0;
    const wraps = [...document.querySelectorAll('#ProductGridContainer .product-card__finance')];
    if (ro) {
      // Zopa's widgets are watched as well as the wrappers: one hidden
      // after its row was levelled leaves the wrapper at the levelled
      // height, so only the widget's own size reports the change.
      const els = [...wraps, ...document.querySelectorAll('#ProductGridContainer .product-card__finance dividebuy-widget')];
      els.forEach((w) => { if (!watched.has(w)) { ro.observe(w); watched.add(w); } });
      watched.forEach((w) => { if (!w.isConnected) { ro.unobserve(w); watched.delete(w); } });
    }
    wraps.forEach((w) => { w.style.minHeight = ''; });
    if (!twoUp.matches || !document.querySelector('#ProductGridContainer .product-card__finance-klarna')) return;
    // Layout positions (offsetTop/offsetHeight), not on-screen ones:
    // cards still waiting for their reveal animation sit translated.
    const rows = new Map();
    wraps.forEach((w) => {
      const card = w.closest('.product-card');
      if (!card || noBand(w)) return;
      const top = card.offsetTop;
      if (!rows.has(top)) rows.set(top, []);
      rows.get(top).push(w);
    });
    rows.forEach((row) => {
      if (row.length < 2) return;
      const tallest = Math.max(...row.map((w) => w.offsetHeight));
      row.forEach((w) => { w.style.minHeight = `${tallest}px`; });
    });
  };
  const schedule = () => { if (!frame) frame = requestAnimationFrame(level); };

  const start = () => {
    if (!document.querySelector('#ProductGridContainer')) return;
    level();
    if (twoUp.addEventListener) twoUp.addEventListener('change', schedule);
    else if (twoUp.addListener) twoUp.addListener(schedule);
    window.addEventListener('resize', schedule);
    // Filter, sort and paging swap the grid's HTML.
    new MutationObserver(schedule).observe(document.querySelector('#ProductGridContainer'), { childList: true, subtree: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();

  // Theme editor: re-rendering the section replaces the grid.
  document.addEventListener('shopify:section:load', schedule);
})();
